import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { SortableContainer } from 'react-sortable-hoc';
import _isEmpty from 'lodash/isEmpty';

import { isTablet } from '@zola-helpers/client';
import { showModal } from 'actions/ModalActions';
import {
  getHomepageSections,
  editHomepageSection,
} from 'actions/website/entities/HomepageSectionActions';
import { resetEntities, addEntity } from 'actions/website/EntityActions';

import { selectDefaultValues } from 'selectors/website/customizationSelectors';

import Accordion from 'components/common/ui/Accordion';
import CustomizableBanner from 'components/manage/website/WebsiteCustomization/components/CustomizableBanner';

import type {
  WCmsHomepageSectionView,
  WThemeCustomizationDefaultView,
  WCmsPageView,
} from '@zola/svc-web-api-ts-client';
import { RootState, AppDispatch } from 'reducers/index';

// Components
import { getUserContext } from 'selectors/user/userSelectors';
import EditPageHeader from '../EditPageCommon/EditPageHeader/EditPageHeader';
import WeddingInfoForm from './components/WeddingInfoForm';
import WeddingDetailsAccordion from './components/WeddingDetailsAccordion';
import HomeSettingsForm from './components/HomeSettingsForm';
import MonogramFooter from './components/MonogramFooter';
import PaperPreviewCrossSellModule from '../../PaperPreviewCrossSellModule';
import EditPageAddEntity from '../EditPageCommon/EditPageAddEntity';
import HomeImageUpload from './components/HomeImageUpload';
import { EntityName } from '../EditPageCommon/EditPageSortableList';

import experimentFlags, { WEBSITE_WEDDING_DATE } from '../../../../../util/experimentFlags';

import sharedStyles from '../EditPageCommon/editPageSharedStyles.module.less';

type HomeProps = HomeStateProps & HomeDispatchProps;

const Home: React.FC<HomeProps> = ({
  customizationDefaults,
  homepageSections,
  orderedIds,
  page,
  themeKey,
  websiteWeddingDateFlag,
  getHomepageSectionEntities,
  unloadEntities,
  onDelete,
  onEdit,
  openModal,
}) => {
  useEffect(() => {
    getHomepageSectionEntities();
    return () => unloadEntities();
  }, []);

  const weddingAccordionItems = [
    {
      heading: 'Wedding Summary',
      body: <WeddingInfoForm {...page} />,
    },
  ];

  const isCustomizable = !_isEmpty(customizationDefaults);
  const showCustomizableBanner = isCustomizable && isTablet();

  const StoryDetails = (entity: WCmsHomepageSectionView): JSX.Element => {
    const story = entity || {};
    const { title } = story;
    return <EntityName name={title} />;
  };

  return (
    <div>
      {themeKey && <EditPageHeader onAdd={openModal} page={page} themeKey={themeKey} />}

      {showCustomizableBanner && <CustomizableBanner />}

      {websiteWeddingDateFlag ? (
        <WeddingDetailsAccordion />
      ) : (
        <div className="wedding-form-accordion">
          <Accordion items={weddingAccordionItems} collapsed />
        </div>
      )}
      <HomeSettingsForm websiteWeddingDateFlag={websiteWeddingDateFlag} />
      <HomeImageUpload imageKey="header" className={sharedStyles.imageUploadSpacing} />
      <PaperPreviewCrossSellModule
        themeKey={themeKey}
        photoUrl={page.header_image_url}
        trackingParams={{
          cta_id: `single_sample_website_edit_homepage`,
          location: 'WEBSITE',
          section: `WEBSITE_EDIT_HOMEPAGE`,
        }}
        showSwatchArrow
      />
      <EditPageAddEntity
        entityName="Story"
        entitiesListTitle="Your Stories"
        pageType="home"
        orderedIds={orderedIds}
        entities={homepageSections}
        onAdd={openModal}
        onEdit={onEdit}
        onDelete={onDelete}
        entityDetail={StoryDetails}
      />
      <HomeImageUpload imageKey="footer" className={sharedStyles.imageUploadSpacing} />
      <MonogramFooter />
    </div>
  );
};

type HomeStateProps = {
  customizationDefaults?: WThemeCustomizationDefaultView;
  homepageSections: Record<number, WCmsHomepageSectionView>;
  orderedIds: number[];
  page: WCmsPageView;
  themeKey?: string;
  websiteWeddingDateFlag?: boolean;
};

const mapStateToProps = (state: RootState): HomeStateProps => ({
  customizationDefaults: selectDefaultValues(state),
  homepageSections: state.website.entities.byId,
  orderedIds: state.website.entities.orderedIds,
  page: state.website.pages.byId[state.website.pages.typeToId.HOME],
  themeKey: state.wedding.theme.key,
  websiteWeddingDateFlag: experimentFlags.getFlag(WEBSITE_WEDDING_DATE, getUserContext(state)),
});

type HomeDispatchProps = {
  getHomepageSectionEntities: () => void;
  unloadEntities: () => void;
  onEdit: (entity: WCmsHomepageSectionView) => void;
  onDelete: (entity: WCmsHomepageSectionView) => void;
  openModal: () => void;
};

const mapDispatchToProps = (dispatch: AppDispatch): HomeDispatchProps => ({
  openModal: () => {
    dispatch(addEntity());
    dispatch(showModal('HOMEPAGE_SECTION', {}, { size: 'md' }));
  },
  getHomepageSectionEntities: () => {
    dispatch(getHomepageSections());
  },
  onEdit: faq => {
    dispatch(editHomepageSection(faq));
    dispatch(showModal('HOMEPAGE_SECTION', { isUpdate: true }, { size: 'md' }));
  },
  onDelete: entity => {
    dispatch(showModal('DELETE_ENTITY', { entity }));
  },
  unloadEntities: () => {
    dispatch(resetEntities());
  },
});

const SortableHomepageSections = SortableContainer(Home);

export default connect(mapStateToProps, mapDispatchToProps)(SortableHomepageSections);
